<script setup lang="ts">
import {computed} from "vue";
import {Minus, Plus} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Switch} from "@/core/components/ui/switch";
import {Textarea} from "@/core/components/ui/textarea";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {AssistantParameterField, AssistantParameterValue} from "@/ai-assistant/interfaces";

const {field, emptyLabel = 'Illimité'} = defineProps<{
    field: AssistantParameterField;
    /**
     * What an integer without a value reads as. « Illimité » suits a daily cap,
     * which is what this field was written for; a spacing cleared to nothing is
     * no spacing at all, and the screens that edit one say so themselves.
     */
    emptyLabel?: string;
}>();

const value = defineModel<AssistantParameterValue>({required: true});

const min = computed<number>(() => field.min ?? 1);
const max = computed<number>(() => field.max ?? 100);
const step = computed<number>(() => field.step ?? 1);

const capped = computed<number | null>(() => typeof value.value === 'number' ? value.value : null);

/**
 * Step the cap, treating the absence of a value as its own state below the
 * minimum: no value means no cap, which is not the same as a cap of zero.
 */
const stepCap = (direction: number) => {
    if (capped.value === null) {
        value.value = direction > 0 ? min.value : null;

        return;
    }

    const next = capped.value + direction * step.value;

    value.value = next < min.value ? null : Math.min(max.value, next);
};
</script>

<template>
  <div v-if="field.type === 'integer'" class="flex items-center gap-2 rounded-lg bg-primary-lightest px-2.5 py-1.5">
    <!--
      Wrapped and not truncated: the label is what says which parameter is
      being stepped, and an ellipsis hid exactly that as soon as the server
      declared one worded as a sentence. The stepper keeps its size — it is
      the row that grows.
    -->
    <span class="min-w-0 flex-1 text-xs leading-snug text-gray-dark" :title="field.help ?? undefined">
      {{ field.label }}
    </span>
    <Button
      type="button"
      variant="outline"
      size="icon"
      class="size-6 shrink-0 rounded-full"
      :disabled="capped === null"
      :aria-label="`Réduire : ${field.label}`"
      @click="stepCap(-1)"
    >
      <Minus class="size-3" />
    </Button>
    <span class="w-14 shrink-0 text-center text-xs font-semibold tabular-nums text-primary">
      {{ capped === null ? emptyLabel : capped }}
    </span>
    <Button
      type="button"
      variant="outline"
      size="icon"
      class="size-6 shrink-0 rounded-full"
      :disabled="capped !== null && capped >= max"
      :aria-label="`Augmenter : ${field.label}`"
      @click="stepCap(1)"
    >
      <Plus class="size-3" />
    </Button>
  </div>

  <div v-else-if="field.type === 'textarea'">
    <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-dark/60">
      {{ field.label }}
    </p>
    <Textarea
      :model-value="(value as string) ?? ''"
      :maxlength="field.max_length ?? undefined"
      class="min-h-20 bg-white text-xs"
      :placeholder="field.help ?? undefined"
      @update:model-value="value = ($event as string) || null"
    />
  </div>

  <div v-else-if="field.type === 'boolean'" class="flex items-center justify-between gap-2 px-0.5">
    <span class="min-w-0 flex-1 text-xs text-gray-dark">{{ field.label }}</span>
    <Switch :model-value="!!value" @update:model-value="value = $event" />
  </div>

  <div v-else-if="field.type === 'select'" class="flex items-center justify-between gap-2 px-0.5">
    <span class="min-w-0 flex-1 text-xs text-gray-dark">{{ field.label }}</span>
    <Select :model-value="(value as string) ?? undefined" @update:model-value="value = ($event as string) ?? null">
      <SelectTrigger class="h-8 w-40 shrink-0 bg-white text-xs">
        <SelectValue placeholder="Choisir…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="option in field.options ?? []" :key="option.value" :value="option.value">
          {{ option.label }}
        </SelectItem>
      </SelectContent>
    </Select>
  </div>

  <!--
    Any other type renders nothing. The server declares the parameter list and
    it grows: a field this renderer does not know must not break the panel.
  -->
</template>
